






































































































import Vue from 'vue'
import AudioRecorderPolyfill from 'audio-recorder-polyfill'
import UserRecordCountdown from '@/components/play/record/UserRecordCountdown.vue'
import * as types from '@/store/modules/play/types'
import * as audioUtil from '@/utils/audio'
import * as timeUtil from '@/utils/time'
import * as localstorageUtil from '@/utils/localstorage'
import { blobToArrayBuffer } from '@/utils/binary'

interface State {
  isMicSupported: boolean;  // マイクが使用できる
  audioContext: AudioContext | null;
  mediaStream: MediaStream | null;
  recorded: {
    arrayBuffer: ArrayBuffer | null;
    audioBuffer: AudioBuffer | null;
  };  // 記憶した音
  playingSounds: AudioBufferSourceNode[];
  // 入力音声のビジュアライズ
  microphoneVisualize: {
    canvasContext: CanvasRenderingContext2D | null; // 音声入力のcanvas表示用
    reqId: number;
    animationStart: number;
  };
  // 周波数のビジュアライズ（テスト用）
  frequencyVisualize: {
    canvasContext: CanvasRenderingContext2D | null; // ビジュアライズ用のcanvas
    reqId: number; // 入力音声のアニメーション用
    animationStart: number;  // 入力音声のアニメーションを間引くため
  };
  recording: {
    progressMSec: number; // 収録中の時間（秒）
    timer: number;  // IntervalTimer
    duration: number; // 収録する秒数
  };
  countdown: {
    timer: number;
  };
  status: {
    isRecording: boolean; // 録音中？
    isRecorded: boolean;  // 録音した
    isCountingDown: boolean;  // カウントダウン中
    hasMediaStream: boolean;  // MediaStreamを取得したか
  };
  env: {
    // レスポンシブ対応のための変数
    deviceWidth: 'small' | 'medium';
  };
  // isContentTransition: boolean; // コンテンツのtransition用
}

export default Vue.extend({
  name: 'UserRecord',

  components: {
    'user-record-countdown': UserRecordCountdown,
  },

  data (): State {
    return {
      isMicSupported: true,
      audioContext: null,
      mediaStream: null,
      recorded: {
        arrayBuffer: null,
        audioBuffer: null,
      },
      playingSounds: [],
      frequencyVisualize: {
        canvasContext: null,
        reqId: NaN,
        animationStart: 0,
      },
      microphoneVisualize: {
        canvasContext: null,
        reqId: NaN,
        animationStart: 0,
      },
      recording: {
        progressMSec: 0,
        timer: NaN,
        duration: 3000,  // 固定
      },
      countdown: {
        timer: NaN,
      },
      status: {
        isRecording: false,
        isRecorded: false,
        isCountingDown: false,
        hasMediaStream: false,
      },
      env: {
        deviceWidth: 'small',
      },
      // isContentTransition: true,
    }
  },

  created () {
    // Safari用にMediaRecorderのPolyfillを設定する
    // Android8以下？では録音時に雑音が混じる原因になるのでiOS以外では使用しない。
    if (!window.MediaRecorder) {
      window.MediaRecorder = AudioRecorderPolyfill
    }
  },

  mounted () {
    // canvasの参照を保持
    const microphoneVisualizeCanvas = this.$refs.microphoneVisualizerCanvas as HTMLCanvasElement
    if (microphoneVisualizeCanvas) {
      this.microphoneVisualize.canvasContext = microphoneVisualizeCanvas.getContext('2d')
    }

    // canvasの参照を保持（テスト用）
    const visualizerCanvas = this.$refs.visualizerCanvas as HTMLCanvasElement
    if (visualizerCanvas) {
      this.frequencyVisualize.canvasContext = visualizerCanvas.getContext('2d')
    }

    // ステータス初期化
    this.status.isRecording = false
    this.status.isRecorded = false
    this.status.hasMediaStream = false

    // メディアクエリーの検知
    const mediaQueryList = window.matchMedia('(max-width: 751px)')
    mediaQueryList.addListener(this.onChangeBreakPoint)
    this.detectDeviceByBreakPoint(mediaQueryList)

    // TODO タップイベントから遅延あるけど大丈夫かな
    this.audioContext = audioUtil.createAudioContext()

    if (this.audioContext) {
      this.start()
    }

    // ページが非表示されたら停止
    // TODO スマホ、タブレットのみ？
    document.addEventListener('visibilitychange', this.onVisibilityChanged)
  },

  beforeDestroy () {
    this.stopSounds()

    document.removeEventListener('visibilitychange', this.onVisibilityChanged)

    // 収録中のタイマーがあればクリア
    if (!isNaN(this.recording.timer)) {
      clearInterval(this.recording.timer)
      this.recording.timer = NaN
    }

    if (this.audioContext && this.audioContext.state === 'running') {
      this.audioContext.close()
    }

    // 音のビジュアライズを停止
    this.stopVisualize()

    // マイクを開放
    if (this.mediaStream) {
      const tracks = this.mediaStream.getTracks() as MediaStreamTrack[]
      tracks.forEach((track) => {
        track.stop()
      })
    }
  },

  computed: {
    /**
     * マイク音声をビジュアライズするときの最小半径（＝マイクボタンのサイズ）
     */
    visualizeMinRadius (): number {
      return this.env.isSizeSm ? 130 : 148
    },
  },

  methods: {
    /**
     * ビジュアライズ、録音準備
     */
    start () {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // 許可を取得
        navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        })
          .then((stream: MediaStream) => {
            // streamを保持
            this.mediaStream = stream

            // 許可された→音声をビジュアライズ
            this.startVisualize(stream)
          })
          .catch((e: Error) => {
            // ユーザーによってマイクが拒否された場合など
            console.error(e)

            // 録音不可
            this.isMicSupported = false

            // // 例外の種類は7種類
            // // see https://developer.mozilla.org/ja/docs/Web/API/MediaDevices/getUserMedia#Exceptions
            // switch (e.name) {
            //   case 'NotFoundError':
            //     alert('マイクが検出できませんでした。')
            //     break
            //
            //   case 'NotAllowedError':
            //     alert('マイクが許可されていません。')
            //     break
            //
            //   case 'NotReadableError':
            //   case 'OverconstrainedError':
            //   case 'SecurityError':
            //   case 'TypeError':
            //   case 'AbortError':
            //     alert('マイクを使用できません。')
            //     break
            // }
          })
      } else {
        // 録音不可
        this.isMicSupported = false
        console.log('navigator.mediaDevices not support')
      }
    },

    /**
     * 音声入力を可視化
     */
    startVisualize (stream: MediaStream) {
      console.log('startVisualize')

      // // AudioContextを生成しておく（storeのaudioContextは使用しない）
      // this.$store.commit(`${types.moduleName}/${types.Mutation.AUDIO_CONTEXT.CREATE}`)

      // 録音用のaudioContextを使う
      const source = this.audioContext.createMediaStreamSource(stream)
      const analyserNode = this.audioContext.createAnalyser()

      analyserNode.fftSize = 2048
      source.connect(analyserNode)

      // 円形のビジュアライズ
      if (this.microphoneVisualize.canvasContext) {
        const draw = (timestamp: number) => {
          const progress = timestamp - this.microphoneVisualize.animationStart  // 進捗

          // requestAnimationFrameは間引く（スマホで実行すると端末がかなり熱くなる）
          if (progress > 33.3) {
            this.microphoneVisualize.animationStart = timestamp

            const bufferLength = analyserNode.frequencyBinCount
            const dataArray = new Uint8Array(bufferLength)
            analyserNode.getByteFrequencyData(dataArray)

            // 周波数をビジュアライズ
            // this.microphoneVisualize.canvasContext.fillStyle = 'rgba(255, 255, 255, 0.5)'
            this.microphoneVisualize.canvasContext.clearRect(0, 0, 400, 400)

            // 音の最大値を取得
            const peakFrequency = dataArray.reduce((a, b) => Math.max(a, b))
            const per = peakFrequency / 255

            // TODO 円の最小値を持っておく必要がある（matchMediaを使用）
            // マイクボタンの外から始まるように
            if (this.status.isRecording) {
              this.microphoneVisualize.canvasContext.fillStyle = 'rgb(255, 108, 132, 0.2)'
            } else {
              this.microphoneVisualize.canvasContext.fillStyle = 'rgb(45, 135, 200, 0.2)'
            }
            this.microphoneVisualize.canvasContext.beginPath()
            this.microphoneVisualize.canvasContext.arc(200, 200, (200 - this.visualizeMinRadius) * per + this.visualizeMinRadius, 0, 360 * Math.PI / 180, false)
            this.microphoneVisualize.canvasContext.closePath()
            this.microphoneVisualize.canvasContext.fill()
          }

          // 繰り返す
          this.visualizeReqId = window.requestAnimationFrame(draw)
        }

        this.microphoneVisualize.reqId = window.requestAnimationFrame(draw)
      }

      // テスト用の周波数表示
      if (this.frequencyVisualize.canvasContext) {
        /**
         * 描画
         */
        const draw = (timestamp: number) => {
          const progress = timestamp - this.frequencyVisualize.animationStart  // 進捗

          // requestAnimationFrameは間引く（スマホで実行すると端末がかなり熱くなる）
          if (progress > 33.3) {
            this.frequencyVisualize.animationStart = timestamp

            const bufferLength = analyserNode.frequencyBinCount
            const dataArray = new Uint8Array(bufferLength)
            analyserNode.getByteFrequencyData(dataArray)

            // 周波数をビジュアライズ
            const barWidth = 400 / bufferLength
            this.frequencyVisualize.canvasContext.fillStyle = 'rgba(245, 245, 245, 1)'
            this.frequencyVisualize.canvasContext.fillRect(0, 0, 400, 200)

            let posX = 0
            for (let i = 0, l = bufferLength; i < l; ++i) {
              // const value = dataArray[i]
              // const percent = value / 255
              // const height = ch * percent
              // const offset = ch - height

              // TODO 一定レベルの音声を検知したら色を変える？

              const per = dataArray[i] / 255
              const barHeight = 200 * per
              if (this.status.isRecording) {
                this.frequencyVisualize.canvasContext.fillStyle = 'rgb(255, 108, 132)'
              } else {
                this.frequencyVisualize.canvasContext.fillStyle = 'rgb(45, 135, 200)'
              }
              this.frequencyVisualize.canvasContext.fillRect(posX, 200 - (per * barHeight), barWidth, per * barHeight)

              // if (i === 0) {
              //   console.log('per', i, dataArray[i] - analyserNode.maxDecibels, per)
              // }

              posX += barWidth + 1
            }
          }

          // 繰り返す
          this.frequencyVisualize.reqId = window.requestAnimationFrame(draw)
        }

        this.frequencyVisualize.reqId = window.requestAnimationFrame(draw)
      }
    },

    /**
     * 音声の可視化を終了
     */
    stopVisualize () {
      console.log('stopVisualize')
      window.cancelAnimationFrame(this.visualizeReqId)
      window.cancelAnimationFrame(this.microphoneVisualize.reqId)
    },

    /**
     * 録音
     * @param stream
     */
    mediaRecord (stream: MediaStream) {
      console.log('mediaRecord')

      const mediaRecorder = new MediaRecorder(this.mediaStream)
      const chunks: Blob[] = []

      this.status.isRecorded = false

      // 音声の可視化を開始
      // これを先に実行しないと、AudioContextが進まない？（Android Chrome）

      mediaRecorder.addEventListener('dataavailable', (e: BlobEvent) => {
        console.count('dataavailable')
        if (e.data.size > 0) {
          chunks.push(e.data)
        }
      })

      mediaRecorder.addEventListener('error', (e: Event) => {
        console.log(e)
      })

      mediaRecorder.addEventListener('start', () => {
        console.log('record start.')

        this.status.isRecording = true
        this.status.isCountingDown = false

        // // きっちり秒数を記録するため、setTimeout(0)でタイミングを遅らせつつ、AudioContextのタイマーで処理する
        // const endTime = ctx.currentTime + 3;  // 3秒後
        // let count = 1;

        // // 一定時間で録音停止
        // const intervalTimer = setInterval(() => {
        //   debug(`source: ${ctx.currentTime >= endTime}, ${ctx.currentTime}, ${endTime} ${count++}`);
        //   if (ctx.currentTime >= endTime) {
        //     console.log(ctx.currentTime - endTime, ctx.currentTime, endTime);
        //     clearInterval(intervalTimer);
        //     mediaRecorder.stop();
        //   }
        // }, 10);

        // 既存のタイマーは停止
        if (!isNaN(this.recording.timer)) {
          clearInterval(this.recording.timer)
        }

        const startTime = new Date().getTime()
        this.recording.timer = setInterval(() => {
          const progressMSec = new Date().getTime() - startTime // 経過時間
          this.recording.progressMSec = progressMSec

          // 一定時間で収録終了
          if (progressMSec >= this.recording.duration) {
            clearInterval(this.recording.timer)
            this.recording.timer = NaN

            // 収録時間を3秒に設定する
            // 実際には3秒に満たない場合があり、表示上2秒になってしまうため
            this.recording.progressMSec = this.recording.duration

            // 収録完了
            mediaRecorder.stop()
          }
        }, 100)
      })

      // 記録完了
      mediaRecorder.addEventListener('stop', () => {
        console.log('record stop.')

        this.status.isRecording = false

        if (chunks.length > 0) {
          // // 分割されたblobデータを一つにする。（timesliceを指定していなければ一つ）
          const blob = new Blob(chunks)

          // 再生テスト
          // blob → arrayBuffer → audioBufferの変換
          blobToArrayBuffer(blob)
            .then((arrayBuffer) => {
              this.recorded.arrayBuffer = arrayBuffer.slice(0)  // 複製をstateに保持。decode後に消える？ため

              console.log(arrayBuffer, this.recorded.arrayBuffer)

              // // 保存
              // localstorageUtil.save(arrayBuffer)

              // 録音した音をAudioBufferに変換
              // SafariはPromiseベースのデコードをサポートしていない
              this.audioContext.decodeAudioData(arrayBuffer,
                (decodedData: AudioBuffer) => {
                  console.log(decodedData)
                  this.recorded.audioBuffer = decodedData
                  this.status.isRecorded = true

                  // // TEST 録音した音を再生
                  // audioUtil.playAudio(this.audioContext, decodedData, 0)

                  // // TEST タイミングをずらして重ねて再生
                  // audioUtil.playAudio(this.audioContext, decodedData, 1)
                  // audioUtil.playAudio(this.audioContext, decodedData, 2)
                  // audioUtil.playAudio(this.audioContext, decodedData, 5)
                },
                (e: Error) => {
                  console.log(e)
                })
            })
            .catch((e) => {
              console.log(e)
            })

          // // TEST 録音をaudioでプレビュー
          // for (let i = 0, l = chunks.length; i < l; i++) {
          //   previewAudio(chunks[i]);
          // }
        } else {
          console.log('chunks is nothing')
        }
      })

      // カウントダウンありの場合は即開始
      mediaRecorder.start()

      // // タップと同時に記録を開始するとAndroid Chromeで録音できないので、setTimeoutで遅らせる
      // setTimeout(() => {
      //   mediaRecorder.start()
      // }, 500)
    },

    /**
     * 録音を開始する
     */
    startRecord () {
      // // 好きなタイミングで音を鳴らせるように＆波形を取得できるように無音を再生しておく（スマホ対策）
      // // この対応でAudioContextのcurrentTimeが進むようになる
      // TOOD 不要？
      // audioUtil.playSilent(this.audioContext)

      // カウントダウン開始
      this.status.isCountingDown = true

      // テスト再生は停止
      this.stopSounds()

      if (!isNaN(this.countdown.timer)) {
        clearTimeout(this.countdown.timer)
      }

      this.countdown.timer = window.setTimeout(() => {
        this.countdown.timer = NaN
        this.mediaRecord(this.mediaStream)
      }, 3000)
    },

    /**
     * テスト再生
     */
    preview () {
      // 停止しておく
      this.stopSounds()

      this.playingSounds.push(audioUtil.playAudio(this.audioContext, this.recorded.audioBuffer, 0))
    },

    // /**
    //  * プレビュー再生を停止
    //  */
    // stopPreview () {
    //   this.stopSounds()
    // },

    /**
     * テスト再生を停止
     */
    stopSounds () {
      this.playingSounds.forEach((audioBufferSourceNode: AudioBufferSourceNode) => {
        audioBufferSourceNode.disconnect()
        audioBufferSourceNode.stop()
      })
      this.playingSounds = []
    },

    /**
     * 録音完了
     */
    done () {
      console.log('done')

      // storeに反映
      this.$store.commit(`${types.moduleName}/${types.Mutation.USER_SOUND.SET}`, this.recorded.audioBuffer)

      // localstorageに保存
      // // TODO ダイアログで確認 or 保存ボタンで保存？
      // if (this.recorded.arrayBuffer && window.confirm('ブラウザに保存しますか？')) {
      //   console.log('保存', localstorageUtil.save(this.recorded.arrayBuffer))
      // }
      localstorageUtil.save(this.recorded.arrayBuffer)

      this.dismiss()
    },

    /**
     * キャンセル
     */
    cancel () {
      this.dismiss()
    },

    /**
     * 閉じる
     */
    dismiss () {
      // テスト再生した音を消す
      this.stopSounds()

      this.$emit('dismiss', true)
    },

    /**
     * ページが非表示された
     * @param e
     */
    onVisibilityChanged (e: Event) {
      const document = e.target as Document
      if (document.visibilityState === 'hidden') {
        this.stopSounds()
      }
    },

    /**
     * ブレイクポイントを通過した
     */
    onChangeBreakPoint (e: MediaQueryListEvent) {
      this.detectDeviceByBreakPoint(e)
    },

    /**
     * ブレイクポイントからデバイスを判別する
     * @param mq メディアクエリー or メディアクエリーイベント
     */
    detectDeviceByBreakPoint (mq: MediaQueryList | MediaQueryListEvent) {
      this.env.deviceWidth = mq.matches ? 'small' : 'medium'
    },
  },

  filters: {
    /**
     * 秒→hh:mm:ss.ss
     * @param sec 秒
     */
    timeFormat (sec: number) {
      return timeUtil.secToTime(sec)
    },
  },
})
